/**
 * Espejo de las reglas de cruce de `core/texto.js`.
 *
 * Igual que `sesion/permisos.ts`, esto **no decide nada**: el motor cruza en el
 * backend y su palabra es la única que cuenta. Aquí sirve para poder avisarle a
 * quien registra un abono que la factura que está eligiendo no va a casar —
 * antes de que la guarde y descubra días después que el pago quedó suelto.
 *
 * Si esta copia y la del backend se desincronizan, el peor caso es un aviso de
 * más o de menos en la pantalla. Nunca un cruce distinto.
 */

const soloDigitos = (s: string) => s.replace(/\D/g, "");

/** Descarta el dígito de verificación cuando viene con guion: 900.123.456-7 → 900123456 */
export function normalizarNit(s: string | null | undefined): string {
  const texto = String(s ?? "").trim();
  if (!texto) return "";
  const conGuion = texto.match(/^([\d.\s]+)-\s*\d$/);
  return soloDigitos(conGuion?.[1] ?? texto);
}

/**
 * Dos identificaciones son la misma tolerando el dígito de verificación.
 *
 * No se resuelve recortando el último dígito —eso rompería las cédulas
 * legítimas de 10 dígitos— sino comparando longitudes: 900123456 y 9001234567
 * pueden ser el mismo NIT con y sin DV.
 */
export function mismoNit(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = normalizarNit(a);
  const y = normalizarNit(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const [corto, largo] = x.length <= y.length ? [x, y] : [y, x];
  return largo.length === corto.length + 1 && largo.startsWith(corto);
}

/** Claves de una factura: la forma completa y la puramente numérica. */
export function clavesFactura(v: string | null | undefined): string[] {
  const s = String(v ?? "").trim().replace(/\s/g, "").toUpperCase();
  if (!s) return [];
  const num = s.replace(/\D/g, "");
  return num && num !== s ? [s, num] : [s];
}

/** ¿El número escrito corresponde a esta factura? Misma regla que `buscarFactura`. */
export function coincideNumero(escrito: string, numeroFactura: string): boolean {
  const claves = new Set(clavesFactura(numeroFactura));
  return clavesFactura(escrito).some((k) => claves.has(k));
}

/**
 * Por qué una factura elegida NO va a cruzar con este abono, si es que no cruza.
 * Devuelve null cuando sí cruzaría.
 */
export function motivoNoCruza(nitAbono: string, nitFactura: string): string | null {
  const ambos = normalizarNit(nitAbono) && normalizarNit(nitFactura);
  if (!ambos) return null; // Si falta uno de los dos, el motor no lo usa para descartar.
  if (mismoNit(nitAbono, nitFactura)) return null;
  return `El NIT del abono (${nitAbono}) y el de la factura (${nitFactura}) no coinciden. El sistema no los va a cruzar.`;
}
